import { ChevronDown, CircleDollarSign, ExternalLink } from "lucide-react";

import type {
  BrokerSnapshot,
  NftMetadata,
  ProtocolSnapshot,
  ProtocolTier,
} from "@stonkland/protocol/api-types";
import { BrokerTraitPanel } from "@web/features/terminal/BrokerTraitPanel";
import { targetChain } from "@stonkland/protocol/chain";
import {
  amountLabel,
  assetColor,
  compactAddress,
  decimalLabel,
} from "@web/features/terminal/presentation";

type TerminalViewProps = {
  account?: string;
  activationTiers: Record<string, number>;
  activationTier: number;
  batchTokens: bigint[];
  broker?: BrokerSnapshot;
  busy?: string;
  currentTier?: ProtocolTier;
  landBalance?: bigint;
  metadata?: NftMetadata;
  ownedTokens: bigint[];
  portfolioAccount?: string;
  protocol?: ProtocolSnapshot;
  protocolLive: boolean;
  selectedTier: number;
  selectedToken?: bigint;
  onActivate: () => Promise<void>;
  onActivateBatch: () => Promise<void>;
  onSelectTier: (tier: number) => void;
  onSelectToken: (tokenId: bigint) => void;
  onToggleBatch: (tokenId: bigint) => void;
};

export function TerminalView({
  account,
  activationTiers,
  activationTier,
  batchTokens,
  broker,
  busy,
  currentTier,
  landBalance,
  metadata,
  ownedTokens,
  portfolioAccount,
  protocol,
  protocolLive,
  selectedTier,
  selectedToken,
  onActivate,
  onActivateBatch,
  onSelectTier,
  onSelectToken,
  onToggleBatch,
}: TerminalViewProps) {
  const displayedToken = selectedToken ? Number(selectedToken) : undefined;
  const tierCost = (tier: number) =>
    protocol?.tiers.find((candidate) => candidate.tier === tier)?.cost;
  const activationCostDue =
    currentTier && selectedTier > activationTier
      ? BigInt(currentTier.cost) -
        (activationTier ? BigInt(tierCost(activationTier) || 0) : 0n)
      : undefined;
  const batchCostDue = batchTokens.reduce((total, tokenId) => {
    if (!currentTier) return total;
    const existingTier = activationTiers[tokenId.toString()] || 0;
    const existingCost = existingTier
      ? BigInt(tierCost(existingTier) || 0)
      : 0n;
    return total + BigInt(currentTier.cost) - existingCost;
  }, 0n);
  return (
    <div className="terminal-grid">
      <article className="panel portfolio-panel">
        <div className="panel-head">
          <div>
            <span>INSIDE THIS BROKER</span>
            <strong>
              {displayedToken
                ? `BROKER #${String(displayedToken).padStart(4, "0")}`
                : "NO BROKER SELECTED"}
            </strong>
          </div>
          {portfolioAccount && (
            <a
              className="icon-button"
              aria-label="Open portfolio on explorer"
              href={`${targetChain.blockExplorers.default.url}/address/${portfolioAccount}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={16} />
            </a>
          )}
        </div>
        {broker?.assets.length ? (
          <div className="asset-table">
            <div className="asset-row table-labels">
              <span>ASSET</span>
              <span>BALANCE</span>
              <span>DELIVERY</span>
              <span>CONTRACT</span>
            </div>
            {broker.assets.map((asset) => (
              <div className="asset-row" key={asset.address}>
                <span className="asset-name" data-label="Asset">
                  <i style={{ background: assetColor(asset.address) }} />
                  <b>{asset.symbol}</b>
                  <small>{asset.name}</small>
                </span>
                <span data-label="Balance">
                  {decimalLabel(asset.formattedBalance)}
                </span>
                <span className="positive" data-label="Delivery">
                  PUSHED ON CLOCK IN
                </span>
                <span data-label="Contract">
                  {compactAddress(asset.address)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state panel-empty">
            Connect a wallet and choose a Broker to see every RWA token inside
            it.
          </div>
        )}
        {broker && <BrokerTraitPanel metadata={metadata} />}
      </article>

      <aside className="panel action-panel" id="put-to-work">
        <div className="panel-head">
          <div>
            <span>WORK STATUS</span>
            <strong>PUT YOUR BROKER TO WORK</strong>
          </div>
          <CircleDollarSign size={18} />
        </div>
        {ownedTokens.length > 0 ? (
          <label className="field">
            <span>OWNED BROKER</span>
            <select
              value={selectedToken?.toString() || ""}
              onChange={(event) => onSelectToken(BigInt(event.target.value))}
            >
              {ownedTokens.map((id) => (
                <option value={id.toString()} key={id.toString()}>
                  Broker #{id.toString().padStart(4, "0")}
                </option>
              ))}
            </select>
            <ChevronDown size={15} />
          </label>
        ) : (
          <div className="empty-state action-empty">
            {account
              ? "This wallet owns no minted Brokers."
              : "Connect a wallet to discover owned Brokers."}
          </div>
        )}
        <div className="position-summary">
          <span>
            <small>LAND BALANCE</small>
            <strong>{amountLabel(landBalance, 2)}</strong>
          </span>
          <span>
            <small>WORK STATUS</small>
            <strong>
              {selectedToken
                ? activationTier
                  ? `WORKING · T${activationTier}`
                  : "OFF SHIFT"
                : "—"}
            </strong>
          </span>
        </div>
        <div className="ticket-block">
          <span className="ticket-label">EARNING TIER</span>
          {protocol?.tiers.length ? (
            <>
              <div className="tier-switcher">
                {protocol.tiers.map((tier) => (
                  <button
                    key={tier.tier}
                    className={selectedTier === tier.tier ? "selected" : ""}
                    onClick={() => onSelectTier(tier.tier)}
                    disabled={!tier.enabled}
                  >
                    T{tier.tier}
                  </button>
                ))}
              </div>
              <div className="tier-detail">
                <span>
                  <b>TIER {currentTier?.tier}</b>
                  {currentTier
                    ? `${amountLabel(BigInt(currentTier.cost), 0)} LAND`
                    : "—"}
                </span>
                <strong>
                  {currentTier
                    ? `${Number(currentTier.weight) / 10_000}× EARNING POWER`
                    : "—"}
                </strong>
              </div>
              {protocol.metrics && (
                <div className="transaction-breakdown compact-breakdown">
                  <span>
                    <small>Due now</small>
                    <b>
                      {activationCostDue !== undefined
                        ? `${amountLabel(activationCostDue, 0)} LAND`
                        : "—"}
                    </b>
                  </span>
                  <span>
                    <small>Burned</small>
                    <b>{protocol.metrics.activationBurnBps / 100}%</b>
                  </span>
                  <span>
                    <small>Treasury</small>
                    <b>{protocol.metrics.activationProtocolBps / 100}%</b>
                  </span>
                  {protocol.metrics.activationRedirectBps > 0 && (
                    <span>
                      <small>Redirected</small>
                      <b>{protocol.metrics.activationRedirectBps / 100}%</b>
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">No live tier configuration.</div>
          )}
          <button
            className="ticket-button"
            onClick={() => void onActivate()}
            disabled={!protocolLive || !selectedToken || !currentTier?.enabled}
          >
            {busy === "activate"
              ? "CONFIRMING…"
              : activationTier
                ? "UPGRADE ROLE"
                : "SEND BROKER TO WORK"}
          </button>
          {activationTier > 0 && (
            <p className="empty-state">
              Your Broker keeps earning until you transfer it. RWA tokens
              already inside stay with the Broker.
            </p>
          )}
        </div>
        <div className="ticket-block batch-work-block">
          <span className="ticket-label">BATCH WORK · TIER {selectedTier}</span>
          <p>
            Choose several Brokers and put them on the same earning tier in
            one go.
          </p>
          <div className="batch-token-grid">
            {ownedTokens.map((tokenId) => {
              const current = activationTiers[tokenId.toString()] || 0;
              const eligible = current < selectedTier;
              const selected = batchTokens.some((id) => id === tokenId);
              return (
                <button
                  key={tokenId.toString()}
                  type="button"
                  aria-pressed={selected}
                  className={selected ? "selected" : ""}
                  disabled={!eligible}
                  onClick={() => onToggleBatch(tokenId)}
                >
                  #{tokenId.toString().padStart(4, "0")}
                  <small>
                    {current
                      ? `T${current} → T${selectedTier}`
                      : `OFF → T${selectedTier}`}
                  </small>
                </button>
              );
            })}
          </div>
          <button
            className="secondary-ticket"
            disabled={!protocolLive || batchTokens.length === 0}
            onClick={() => void onActivateBatch()}
          >
            {busy === "activate-batch"
              ? "ACTIVATING BATCH…"
              : `ACTIVATE ${batchTokens.length} BROKER${batchTokens.length === 1 ? "" : "S"}`}
          </button>
          {batchTokens.length > 0 && (
            <p className="batch-fee-due">
              Total activation cost: <b>{amountLabel(batchCostDue, 0)} LAND</b>
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}
